import { useEffect, useMemo, useRef } from 'react';
import type { Dispatch } from 'react';
import type { Action, AppState } from '@ce/core';

/**
 * Browser history.
 *
 * The app is a single scroll container with a reducer, so without this the
 * browser Back button leaves the site entirely — the first thing anyone does
 * after opening a talent profile on mobile web.
 *
 * The URL is derived from state, never the other way round. State changes push
 * a history entry; Back and Forward parse the URL and dispatch to reach it.
 * A ref guards the round trip so an incoming pop does not immediately push the
 * entry it just arrived at.
 */

const ROUTED_SCREENS = new Set([
  'home', 'schedule', 'timeline', 'talent', 'events',
  'hall', 'quests', 'me', 'cart', 'search',
]);

/** State to URL. Onboarding is deliberately unrouted — it is not a destination. */
export function routeOf(state: AppState): string {
  if (state.screen === 'onboard') return '';

  let base: string;
  if (state.screen === 'talentDetail' && state.talentId) base = `talent/${state.talentId}`;
  else if (state.screen === 'sessionDetail' && state.sessionId) base = `session/${state.sessionId}`;
  else base = state.screen;

  // The booking sheet is a modal, and on mobile web Back is how people expect
  // to dismiss one. Giving it its own entry makes that work.
  return state.booking ? `${base}/book` : base;
}

/** URL to the actions that reach it. Needs current state to know what to undo. */
function actionsForRoute(route: string, state: AppState): Action[] {
  const [head, arg, modal] = route.split('/');
  const out: Action[] = [];

  if (head === 'talent' && arg) out.push({ type: 'openTalent', id: arg });
  else if (head === 'session' && arg) out.push({ type: 'openSession', id: arg });
  else if (ROUTED_SCREENS.has(head)) out.push({ type: 'go', screen: head as AppState['screen'] });

  const wantsBooking = modal === 'book';
  if (!wantsBooking && state.booking) out.push({ type: 'closeBooking' });

  return out;
}

export function useHistoryRouting(state: AppState, dispatch: Dispatch<Action>) {
  const route = useMemo(() => routeOf(state), [state]);
  // Set while applying a pop, so the write effect below skips that turn.
  const applying = useRef(false);
  const first = useRef(true);
  const stateRef = useRef(state);
  stateRef.current = state;

  // State -> URL
  useEffect(() => {
    if (applying.current) {
      applying.current = false;
      return;
    }
    const current = window.location.hash.replace(/^#\/?/, '');
    if (current === route) return;

    // The first write replaces rather than pushes, so Back from the opening
    // screen leaves the site instead of landing on a blank entry.
    const url = route ? `#/${route}` : '#/';
    if (first.current) {
      first.current = false;
      window.history.replaceState({ route }, '', url);
    } else {
      window.history.pushState({ route }, '', url);
    }
  }, [route]);

  // URL -> state, on load and on Back/Forward
  useEffect(() => {
    const apply = () => {
      const hash = window.location.hash.replace(/^#\/?/, '');
      const next = hash || 'home';
      if (next === routeOf(stateRef.current)) return;

      const actions = actionsForRoute(next, stateRef.current);
      if (!actions.length) return;

      applying.current = true;
      actions.forEach(dispatch);
    };

    apply();
    window.addEventListener('popstate', apply);
    // hashchange covers a pasted or edited URL, which popstate does not.
    window.addEventListener('hashchange', apply);
    return () => {
      window.removeEventListener('popstate', apply);
      window.removeEventListener('hashchange', apply);
    };
  }, [dispatch]);
}
